/**
 * Elation note TEMPLATE NAME → our canonical visit-note type.
 *
 * This lives in the host, next to the rest of the Elation adapter, and
 * deliberately NOT in `@astrana/ui`. The library is EHR-agnostic and is embedded
 * by several frontends; teaching it to recognize "Simple Note" / "SOAP Note"
 * would bake one EHR's naming into every host of every EHR. The note model stays
 * generic there and `<VisitNoteDrafter openNoteType>` takes the ANSWER — the
 * mapping from an EHR's own vocabulary is the adapter's job, which is here.
 *
 * The name itself comes off the page as `templateName` (see `bridge/types.ts`):
 * `getCurrFormTemplate().displayName` for the OPEN note, `getTemplate()` for
 * listed ones.
 *
 * That value is FREE TEXT from the EHR — practices rename templates, and the
 * casing, spacing and punctuation are whatever someone typed. So it is matched
 * case-insensitively on word boundaries in a punctuation-normalized string, and
 * anything unrecognized returns `undefined` rather than a guess: the drafter
 * then keeps its existing snapshot-derived default. A wrong type costs a
 * wrongly-shaped note; no match costs one click.
 */
import type { VisitNoteType } from "@astrana/ui/scribe";

const MEAT_PATTERN = /\bmeat\b/i;
// `E/M` is the more common clinical spelling than `E&M`, and normalization
// turns it into `E M` — so requiring a literal `&` matched neither it, nor a
// bare `EM`, nor `Evaluation & Management`. A practice naming its template any
// of those three fell through, and every E/M encounter defaulted to a Simple
// note: the exact failure this mapping exists to prevent, fixed for SOAP and
// Simple but not for E/M. `\bem\b` is safe against `Emergency`/`Employment` —
// no word boundary follows the `em` there.
// NOTE the separator is OPTIONAL here, unlike the library's copy of this
// pattern: normalization above has already turned "E/M" into "E M", so a
// pattern demanding a literal separator can never fire on the most common
// spelling. The two copies normalize differently and must be read in context —
// they are a known duplication, tracked for collapse.
const EM_PATTERN = /\be\s*[&/]?\s*m\b|\bem\b|\bevaluation\s+(and|&)\s+management\b/i;
const SOAP_PATTERN = /\bsoap\b/i;
const SIMPLE_PATTERN = /\bsimple\b/i;

export function visitNoteTypeFromTemplateName(
  templateName?: string | null,
): VisitNoteType | undefined {
  // Not just a null check: the bridge payload is untyped JSON the host CASTS,
  // so a non-string can reach here and `.replace` would throw inside a render.
  if (typeof templateName !== "string") return undefined;

  // Punctuation → spaces, so "Simple-Note", "SOAP/Note" and "  simple   note "
  // all match the same way. `&` survives, because "E&M" needs it.
  const name = templateName.replace(/[^a-z0-9&]+/gi, " ").trim();
  if (!name) return undefined;

  // MEAT and E&M first: they are documentation STYLES that ride on whatever
  // template the practice built, so a "MEAT SOAP note" is a MEAT note. The
  // drafter applies the same precedence to the visit type.
  if (MEAT_PATTERN.test(name)) return "MEAT";
  if (EM_PATTERN.test(name)) return "EM";
  if (SOAP_PATTERN.test(name)) return "SOAP";
  if (SIMPLE_PATTERN.test(name)) return "SIMPLE";
  return undefined;
}
